/**
 * Preview-only filters (W2-02; contract core/engine/types.ts `PreviewFilters`, state/types.ts `ViewFilterState`).
 *
 * The editor store keeps the filters the Lines panel and the layer chips set (hidden, solo, muted layers, dim,
 * focus hitsounds, locked); the engine takes them as `PreviewFilters`. Hidden, solo, muted layers and dim change
 * what the preview draws, so the "Preview differs from export" banner (this package's status item, the only
 * implementation of that banner) shows while any is active; locked lines and focus hitsounds do not count.
 */

import type { PreviewFilters } from '../../core/engine/types';
import type { LineId } from '../../core/model/types';
import type { ViewFilterState } from '../../state/types';

/** No filter at all (test play shows the real game). */
export const NO_FILTERS: PreviewFilters = Object.freeze({
  hiddenLines: new Set<LineId>(),
  soloLines: new Set<LineId>(),
  mutedLayers: new Map<LineId, ReadonlySet<string>>(),
  dim: null,
  focusHitsoundLines: new Set<LineId>(),
});

let memo: { f: ViewFilterState; active: LineId | null; out: PreviewFilters } | null = null;

/**
 * The engine filters of the editor state. Memoised on the filter state's identity and the active line, so frames
 * pass the same object while nothing changed.
 */
export function previewFiltersOf(f: ViewFilterState, activeLine: LineId | null): PreviewFilters {
  if (memo?.f === f && memo.active === activeLine) return memo.out;
  const out: PreviewFilters = {
    hiddenLines: f.hiddenLines,
    soloLines: f.soloLines,
    mutedLayers: f.mutedLayers,
    dim: f.dimOthers > 0 && activeLine !== null ? { activeLine, factor: Math.min(1, f.dimOthers) } : null,
    focusHitsoundLines: f.focusHitsoundLines,
  };
  memo = { f, active: activeLine, out };
  return out;
}

export interface FilterSummary {
  readonly hidden: number;
  readonly solo: number;
  readonly muted: number;
  readonly dim: boolean;
}

/** What makes the preview differ from the export (counts per kind). */
export function filterSummary(f: ViewFilterState): FilterSummary {
  let muted = 0;
  for (const set of f.mutedLayers.values()) muted += set.size;
  return { hidden: f.hiddenLines.size, solo: f.soloLines.size, muted, dim: f.dimOthers > 0 };
}

/** True while a preview-changing filter is active (banner). */
export function previewDiffers(f: ViewFilterState): boolean {
  const s = filterSummary(f);
  return s.hidden > 0 || s.solo > 0 || s.muted > 0 || s.dim;
}

/** A line the preview does not draw because of the filters (hidden, or not soloed while a solo is active). */
export function filteredOut(f: Pick<PreviewFilters, 'hiddenLines' | 'soloLines'>, line: LineId): boolean {
  return f.hiddenLines.has(line) || (f.soloLines.size > 0 && !f.soloLines.has(line));
}
